import { LeadStage, SenderDto, SendingSettingsDto } from "@growth/dto";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useActive } from "./active";
import { resetSample, sample, type SampleRef } from "./leads-sample";

/**
 * Leads data for the active brand: accounts, people, outreach and sending. These still go to the
 * in-browser sample (lib/leads-sample.ts); later phases swap the bodies for `api` calls with the
 * same shapes. The ideal customer profile is real already: see lib/icp.ts.
 */

const useLeadsRef = () => {
  const { brand, me } = useActive();
  const ref: SampleRef = { brandId: brand?.id ?? "none", brandName: brand?.name ?? "" };
  const by = me?.user.name ?? me?.user.email ?? "You";
  return { ref, by, key: ["leads", ref.brandId] as const };
};

const useLeadsQuery = <T>(path: string[], fn: (ref: SampleRef) => Promise<T>, enabled = true) => {
  const { ref, key } = useLeadsRef();
  return useQuery({ queryKey: [...key, ...path], queryFn: () => fn(ref), enabled });
};

const useLeadsMutation = <I, O>(fn: (ref: SampleRef, input: I, by: string) => Promise<O>) => {
  const { ref, by, key } = useLeadsRef();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: I) => fn(ref, input, by),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  });
};

// ─── Reads ───────────────────────────────────────────────────────────────────────────────────

export const useLeadsOverview = () => useLeadsQuery(["overview"], sample.overview);
export const useLeadAccounts = () => useLeadsQuery(["accounts"], sample.accounts);
export const useLeadAccount = (id: string) =>
  useLeadsQuery(["accounts", id], (ref) => sample.account(ref, id));
export const useLeadPeople = () => useLeadsQuery(["people"], sample.people);
export const useLeadPerson = (id: string) =>
  useLeadsQuery(["people", id], (ref) => sample.person(ref, id), Boolean(id));
export const useOutreach = () => useLeadsQuery(["messages"], sample.messages);
export const useOutreachMessage = (id: string) =>
  useLeadsQuery(["messages", id], (ref) => sample.message(ref, id));
export const useSendingSettings = () => useLeadsQuery(["settings"], sample.settings);

// ─── Accounts and people ─────────────────────────────────────────────────────────────────────

export const useSetAccountStatus = () =>
  useLeadsMutation(
    (
      ref,
      input: { id: string; status: "suggested" | "approved" | "held" | "excluded"; note: string },
    ) => sample.setAccountStatus(ref, input.id, input.status, input.note),
  );
export const useFindAccounts = () =>
  useLeadsMutation((ref, _input: void) => sample.findAccounts(ref));
export const useSetStage = () =>
  useLeadsMutation((ref, input: { id: string; stage: LeadStage }) =>
    sample.setStage(ref, input.id, input.stage),
  );
export const useSetLinkedIn = () =>
  useLeadsMutation((ref, input: { id: string; field: "sent" | "accepted"; on: boolean }, by) =>
    sample.setLinkedIn(ref, input.id, input.field, input.on, by),
  );
export const useAddNote = () =>
  useLeadsMutation((ref, input: { id: string; text: string }) =>
    sample.addNote(ref, input.id, input.text),
  );
export const useLogReply = () =>
  useLeadsMutation((ref, input: { id: string; text: string }) =>
    sample.logReply(ref, input.id, input.text),
  );

// ─── Outreach ────────────────────────────────────────────────────────────────────────────────

export const useCreateDraft = () =>
  useLeadsMutation((ref, personId: string) => sample.createDraft(ref, personId));
export const useUpdateMessage = () =>
  useLeadsMutation((ref, input: { id: string; subject: string; body: string; senderId?: string }) =>
    sample.updateMessage(ref, input.id, input),
  );
export const useSendBack = () =>
  useLeadsMutation((ref, input: { id: string; note: string }) =>
    sample.sendBack(ref, input.id, input.note),
  );
export const useDeleteDraft = () =>
  useLeadsMutation((ref, id: string) => sample.deleteDraft(ref, id));
export const useSendMessage = () =>
  useLeadsMutation((ref, id: string, by) => sample.send(ref, id, by));

// ─── Sending ─────────────────────────────────────────────────────────────────────────────────

export const useSaveSendingSettings = () =>
  useLeadsMutation((ref, patch: Partial<SendingSettingsDto>) => sample.saveSettings(ref, patch));
export const useSaveSender = () =>
  useLeadsMutation((ref, sender: SenderDto) => sample.saveSender(ref, sender));
export const useSuppress = () =>
  useLeadsMutation((ref, input: { email: string; reason: string }) =>
    sample.suppress(ref, input.email, input.reason),
  );
export const useUnsuppress = () =>
  useLeadsMutation((ref, email: string) => sample.unsuppress(ref, email));

/** Puts this brand's sample back to how it started. */
export const useResetSample = () => {
  const { ref, key } = useLeadsRef();
  const queryClient = useQueryClient();
  return () => {
    resetSample(ref.brandId);
    void queryClient.invalidateQueries({ queryKey: key });
  };
};
